import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import useApiGet from '../hooks/getData';
import useApiPost from '../hooks/postData';
import toast from 'react-hot-toast';

function RoomMembers({ open, handleClose, room }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hostCoins, setHostCoins] = useState(0);
  const [blockingId, setBlockingId] = useState(null);

  const { getData } = useApiGet();
  const { postData } = useApiPost();

  const fetchMembers = async () => {
    if (!room?.id) return;
    setLoading(true);
    try {
      const res = await getData(`/rooms/${room.id}/participants`);
      setMembers(res?.data?.participants || []);
    } catch (err) {
      console.error("Error fetching members:", err);
      toast.error("Failed to load members");
    } finally {
      setLoading(false);
    }
  };

  const fetchHostCoins = async () => {
    if (!room?.id) return;
    try {
      const res = await getData(`/rooms/${room.id}/weekly-history`);
      const total = (res?.data || []).reduce((sum, h) => sum + (h.coin_cost || 0), 0);
      setHostCoins(total);
    } catch (err) {
      console.error("Error fetching host coins:", err);
    }
  };

  useEffect(() => {
    if (open) {
      fetchMembers();
      fetchHostCoins();
    } else {
      setMembers([]);
      setHostCoins(0);
    }
  }, [open, room]);

  const handleBlock = async (userId) => {
    setBlockingId(userId);
    try {
      const res = await postData(`/rooms/${room.id}/participants/${userId}/admin-block`, { blocked: true });
      if (res?.success) {
        toast.success(res?.message || "User blocked successfully");
        fetchMembers();
      } else {
        toast.error(res?.message || "Failed to block user");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setBlockingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogContent className="dark:bg-primary">

        {/* Header */}
        <div className="flex items-start justify-between pb-4 border-b border-gray-200 dark:border-[#1F1F1F]">
          <div>
            <h2 className="text-lg font-semibold font-poppins text-[#000000] dark:text-darkText">
              {room?.name || "Room"}
            </h2>
            <p className="text-xs font-poppins text-[#777777] pt-1">
              {members.length} member{members.length !== 1 ? "s" : ""} • Host: {room?.host_name || "-"}
            </p>
            <div className="pt-2">
              <span className="px-3 py-1.5 text-sm font-semibold text-[#0D9947] bg-[#0D9947] bg-opacity-15 rounded-lg font-poppins">
              🪙 This Week's Earnings: {hostCoins}
              </span>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-2xl text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        </div>

        {/* Members */}
        <div className="mt-3 max-h-[420px] overflow-y-auto custom-scrollbar">
          {loading ? (
            <p className="py-10 text-sm text-center font-poppins dark:text-darkText">Loading members...</p>
          ) : members.length > 0 ? (
            members.map((m) => (
              <div
                key={m.user_id}
                className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-[#1F1F1F]"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={m.avatar}
                    alt={m.name}
                    className="object-cover w-10 h-10 rounded-full bg-gray-100"
                  />
                  <div>
                    <p className="text-sm font-medium font-poppins text-[#00162e] dark:text-darkText">
                      {m.name}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`px-2 py-0.5 text-[10px] rounded-full font-poppins ${
                        m.role === "speaker"
                          ? "text-[#593A99] bg-[#593A99] bg-opacity-10"
                          : "text-[#777777] bg-[#777777] bg-opacity-10"
                      }`}>
                        {m.role}
                      </span>
                      {m.is_muted && (
                        <span className="px-2 py-0.5 text-[10px] rounded-full font-poppins text-[#E89A00] bg-[#E89A00] bg-opacity-10">
                          muted
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {m.is_blocked ? (
                  <span className="px-3 py-1.5 text-xs font-poppins text-[#D92D20] bg-[#D92D20] bg-opacity-10 rounded-lg">
                    Blocked
                  </span>
                ) : (
                  <button
                    onClick={() => handleBlock(m.user_id)}
                    disabled={blockingId === m.user_id}
                    className="px-4 py-1.5 text-xs font-poppins text-[#D92D20] border border-[#D92D20] rounded-lg hover:bg-[#D92D20] hover:text-white transition-all disabled:opacity-50"
                  >
                    {blockingId === m.user_id ? "Blocking..." : "Block"}
                  </button>
                )}
              </div>
            ))
          ) : (
            <p className="py-10 text-sm text-center font-poppins text-[#777777]">
              No members in this room
            </p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default RoomMembers;